// Testimonial2.js - Contains functions for creating a testimonial slider section with unique card design
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a Testimonial section with slider functionality for modern testimonial cards
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The testimonial section elements
 */
export const createTestimonialElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const testimonialSectionClass = 'testimonial2-section';
  const testimonialContainerClass = 'testimonial2-container';
  const testimonialHeaderClass = 'testimonial2-header';
  const testimonialTitleClass = 'testimonial2-title';
  const testimonialSubtitleClass = 'testimonial2-subtitle';
  const testimonialSliderClass = 'testimonial2-slider';
  const testimonialSlideClass = 'testimonial2-slide';
  const testimonialCardClass = 'testimonial2-card';
  const testimonialCardInnerClass = 'testimonial2-card-inner';
  const testimonialControlsClass = 'testimonial2-controls';
  
  // Desktop Styles (default)
  const testimonialSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f8fafc',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const testimonialContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '60px'
  };

  const testimonialHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '0',
    width: '100%',
    maxWidth: '800px'
  };

  const testimonialTitleStyles = {
    fontSize: '42px',
    fontWeight: '800',
    color: '#1e293b',
    marginBottom: '20px',
    lineHeight: '1.2'
  };

  const testimonialSubtitleStyles = {
    fontSize: '18px',
    color: '#64748b',
    lineHeight: '1.6',
    marginBottom: '0'
  };

  const testimonialSliderStyles = {
    position: 'relative',
    width: '100%',
    height: '420px',
    overflow: 'hidden',
    backgroundColor: 'transparent'
  };

  const testimonialSlideStyles = {
    flex: '0 0 50%',
    height: '100%',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: '10px'
  };

  const testimonialCardStyles = {
    width: '100%',
    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.04)',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    position: 'relative',
    border: '1px solid #e2e8f0',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: '0 15px 40px rgba(0,0,0,0.08)'
    }
  };

  const testimonialCardInnerStyles = {
    padding: '40px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  };

  const testimonialControlsStyles = {
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '30px',
    padding: '12px 20px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
    border: '1px solid #e2e8f0'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(testimonialSectionClass, testimonialSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialContainerClass, testimonialContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialHeaderClass, testimonialHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialTitleClass, testimonialTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialSubtitleClass, testimonialSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialSliderClass, testimonialSliderStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialSlideClass, testimonialSlideStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialCardClass, testimonialCardStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialCardInnerClass, testimonialCardInnerStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialControlsClass, testimonialControlsStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    testimonialSectionClass,
    testimonialContainerClass,
    testimonialHeaderClass,
    testimonialTitleClass,
    testimonialSubtitleClass,
    testimonialSliderClass,
    testimonialSlideClass,
    testimonialCardClass,
    testimonialCardInnerClass,
    testimonialControlsClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = sectionId + 1;
  
  // Header elements
  const headerId = sectionId + 2;
  const titleId = sectionId + 3;
  const subtitleId = sectionId + 4;
  
  // Slider elements
  const sliderId = sectionId + 5;
  const maskId = sliderId + 1;
  const leftArrowId = sliderId + 2;
  const rightArrowId = sliderId + 3;
  
  // Controls elements
  const controlsId = sliderId + 4;
  const autoplayToggleId = sliderId + 5;
  const loopToggleId = sliderId + 6;
  
  // Slide IDs with large gaps to avoid collisions
  const slide1Id = sliderId + 100;
  const slide2Id = sliderId + 200;
  const slide3Id = sliderId + 300;
  const slide4Id = sliderId + 400;

  // Card IDs with large gaps to avoid collisions
  const card1Id = slide1Id + 1000;
  const card2Id = slide2Id + 1000;
  const card3Id = slide3Id + 1000;
  const card4Id = slide4Id + 1000;

  // Array of testimonial data
  const testimonials = [
    {
      name: 'Alex Morgan',
      position: 'Product Manager',
      company: 'Innovate Solutions',
      quote: 'This website builder exceeded all my expectations. We were able to launch our product site in days instead of weeks. The templates are modern and the customization options are extensive.',
      avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
      rating: 5,
      accent: '#4f46e5' // Indigo accent
    },
    {
      name: 'Samantha Torres',
      position: 'E-commerce Director',
      company: 'Style Haven',
      quote: 'As an e-commerce business, our website needs were complex. This platform made it simple to create a beautiful storefront that converts visitors into customers. The support team is also incredibly responsive.',
      avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
      rating: 5,
      accent: '#06b6d4' // Cyan accent
    },
    {
      name: 'David Chen',
      position: 'Founder & CEO',
      company: 'Future Labs',
      quote: 'We tried several website builders before finding this one. The intuitive interface and powerful features helped us create a stunning website that perfectly represents our brand. Highly recommended!',
      avatar: 'https://randomuser.me/api/portraits/men/62.jpg',
      rating: 4,
      accent: '#ec4899' // Pink accent
    },
    {
      name: 'Olivia Wilson',
      position: 'Creative Director',
      company: 'Design Collective',
      quote: 'As designers, we have high standards for our digital presence. This platform allowed us to create a portfolio site that showcases our work beautifully. The design flexibility is unmatched.',
      avatar: 'https://randomuser.me/api/portraits/women/29.jpg',
      rating: 5,
      accent: '#f97316' // Orange accent
    }
  ];

  // Create a simple slide with a card child
  const createSlide = (slideId, cardId, index) => {
    return {
      id: slideId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Slide ${index}`,
      parentId: maskId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialSlideClass,
      sliderRole: 'slide',
      styles: {
        flex: '0 0 50%',
        height: '100%'
      },
      text: '',
      children: [cardId], // Each slide has exactly ONE child - its card
      visibility: 'visible',
      isContainer: true,
      attributes: {
        'data-element': 'testimonial-slide',
        'data-slide-index': index - 1
      }
    };
  };

  // Create simple slides
  const slide1 = createSlide(slide1Id, card1Id, 1);
  const slide2 = createSlide(slide2Id, card2Id, 2);
  const slide3 = createSlide(slide3Id, card3Id, 3);
  const slide4 = createSlide(slide4Id, card4Id, 4);

  // Create a card element with a unique design
  const createCard = (id, slideId, testimonialData, index) => {
    // Generate unique ID base for child elements
    const baseId = id * 10;
    
    // IDs for card children
    const cardInnerId = baseId + 1;
    const quoteContainerId = baseId + 2;
    const quoteIconId = baseId + 3;
    const quoteTextId = baseId + 4;
    const authorContainerId = baseId + 5;
    const avatarId = baseId + 6;
    const authorInfoId = baseId + 7;
    const nameId = baseId + 8;
    const positionId = baseId + 9;
    const ratingId = baseId + 10;
    const accentId = baseId + 11;
    
    // Calculate elements for this card
    const elements = [];
    
    // 1. Main card
    elements.push({
      id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Testimonial Card ${index}`,
      parentId: slideId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialCardClass,
      styles: {},
      text: '',
      children: [cardInnerId, accentId],
      visibility: 'visible',
      isContainer: true,
      attributes: {
        'data-element': 'testimonial-card',
        'data-testimonial-index': index
      }
    });
    
    // 2. Card inner container
    elements.push({
      id: cardInnerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Card Inner ${index}`,
      parentId: id,
      hierarchyName: getHierarchyName('div'),
      className: testimonialCardInnerClass,
      styles: {},
      text: '',
      children: [quoteContainerId, authorContainerId],
      visibility: 'visible',
      isContainer: true,
      attributes: {
        'data-element': 'testimonial-card-inner'
      }
    });
    
    // 3. Accent color strip at top
    elements.push({
      id: accentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Card Accent ${index}`,
      parentId: id,
      hierarchyName: getHierarchyName('div'),
      styles: {
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        height: '6px',
        backgroundColor: testimonialData.accent,
      },
      text: '',
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-accent'
      }
    });
    
    // 4. Quote container
    elements.push({
      id: quoteContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Quote Container ${index}`,
      parentId: cardInnerId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        marginBottom: '30px',
        position: 'relative'
      },
      text: '',
      children: [quoteIconId, quoteTextId],
      visibility: 'visible',
      attributes: {
        'data-element': 'quote-container'
      }
    });
    
    // 5. Quote icon
    elements.push({
      id: quoteIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Quote Icon ${index}`,
      parentId: quoteContainerId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        fontSize: '40px',
        color: testimonialData.accent,
        opacity: '0.2',
        position: 'absolute',
        top: '-20px',
        left: '-10px',
        fontFamily: 'Georgia, serif',
        fontWeight: 'bold'
      },
      text: '"',
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'quote-icon',
        'aria-hidden': 'true'
      }
    });
    
    // 6. Quote text
    elements.push({
      id: quoteTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: `Quote Text ${index}`,
      parentId: quoteContainerId,
      hierarchyName: getHierarchyName('p'),
      styles: {
        fontSize: '16px',
        lineHeight: '1.8',
        color: '#475569',
        margin: '0',
        position: 'relative',
        zIndex: '1',
        fontStyle: 'italic'
      },
      text: testimonialData.quote,
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'quote-text'
      }
    });
    
    // 7. Author container
    elements.push({
      id: authorContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Author Container ${index}`,
      parentId: cardInnerId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        alignItems: 'center',
        marginTop: 'auto',
        paddingTop: '20px',
        borderTop: '1px solid #e2e8f0'
      },
      text: '',
      children: [avatarId, authorInfoId],
      visibility: 'visible',
      attributes: {
        'data-element': 'author-container'
      }
    });
    
    // 8. Avatar
    elements.push({
      id: avatarId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: `Author Avatar ${index}`,
      parentId: authorContainerId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        objectFit: 'cover',
        marginRight: '15px',
        border: `2px solid ${testimonialData.accent}`
      },
      src: testimonialData.avatar,
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'author-avatar',
        'alt': testimonialData.name
      }
    });
    
    // 9. Author info container
    elements.push({
      id: authorInfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Author Info ${index}`,
      parentId: authorContainerId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        flexDirection: 'column'
      },
      text: '',
      children: [nameId, positionId, ratingId],
      visibility: 'visible',
      attributes: {
        'data-element': 'author-info'
      }
    });
    
    // 10. Author name
    elements.push({
      id: nameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: `Author Name ${index}`,
      parentId: authorInfoId,
      hierarchyName: getHierarchyName('h4'),
      styles: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#1e293b',
        margin: '0 0 2px 0'
      },
      text: testimonialData.name,
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'author-name'
      }
    });
    
    // 11. Author position
    elements.push({
      id: positionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: `Author Position ${index}`,
      parentId: authorInfoId,
      hierarchyName: getHierarchyName('p'),
      styles: {
        fontSize: '14px',
        color: '#64748b',
        margin: '0 0 5px 0'
      },
      text: `${testimonialData.position}, ${testimonialData.company}`,
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'author-position'
      }
    });
    
    // 12. Rating
    elements.push({
      id: ratingId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Rating ${index}`,
      parentId: authorInfoId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        gap: '2px',
        color: testimonialData.accent,
        fontSize: '14px'
      },
      text: '★'.repeat(testimonialData.rating),
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-rating',
        'data-rating': testimonialData.rating
      }
    });
    
    return elements;
  };
  
  // Create cards for each slide
  const card1Elements = createCard(card1Id, slide1Id, testimonials[0], 1);
  const card2Elements = createCard(card2Id, slide2Id, testimonials[1], 2);
  const card3Elements = createCard(card3Id, slide3Id, testimonials[2], 3);
  const card4Elements = createCard(card4Id, slide4Id, testimonials[3], 4);
  
  // Create slider arrow as link with image
  const createArrow = (id, direction) => {
    // Create unique ID for the image child
    const imageId = id + 1000; // Add 1000 to avoid ID collisions
    
    return {
      id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      sliderRole: 'arrow',
      direction: direction,
      text: '',
      styles: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '46px',
        height: '46px',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
        cursor: 'pointer',
        zIndex: 5,
        textDecoration: 'none',
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor: '#f8fafc',
          transform: 'translateY(-50%) scale(1.05)',
          boxShadow: '0 12px 30px rgba(0,0,0,0.1)'
        },
        // Position left or right arrow accordingly
        left: direction === 'left' ? '15px' : 'auto',
        right: direction === 'right' ? '15px' : 'auto'
      },
      children: [imageId], // Reference to the image element
      parentId: sliderId,
      visibility: 'visible',
      hierarchyName: getHierarchyName('link'),
      interactions: {
        events: [
          {
            id: Date.now() + (direction === 'left' ? 1 : 2),
            type: 'click',
            action: 'custom',
            label: `${direction === 'left' ? 'Previous' : 'Next'} slide`,
            value: {
              script: `
                const slider = event.target.closest('[data-element-type="slider"]');
                if (!slider) return;
                
                const mask = slider.querySelector('[data-slider-role="mask"]');
                if (!mask) return;
                
                // Get device-specific settings based on current screen size
                let slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || 2);
                let slidesPerScroll = parseInt(slider.getAttribute('data-slides-per-scroll') || 1);
                
                // Adjust for mobile
                if (window.innerWidth <= 768) {
                  slidesPerView = 1;
                }
                
                // Get loop setting
                const loopEnabled = slider.getAttribute('data-loop') === 'true';
                
                const slides = mask.children.length;
                let currentSlide = parseInt(mask.getAttribute('data-current-slide') || 0);
                
                if ('${direction}' === 'left') {
                  if (currentSlide === 0 && loopEnabled) {
                    // If at the beginning and loop is enabled, go to the end
                    currentSlide = slides - slidesPerView;
                  } else {
                    // Otherwise, navigate normally
                    currentSlide = Math.max(0, currentSlide - slidesPerScroll);
                  }
                } else {
                  if (currentSlide >= slides - slidesPerView && loopEnabled) {
                    // If at the end and loop is enabled, go to the beginning
                    currentSlide = 0;
                  } else {
                    // Otherwise, navigate normally
                    currentSlide = Math.min(slides - slidesPerView, currentSlide + slidesPerScroll);
                  }
                }
                
                // Apply transition for smooth movement
                mask.style.transition = 'transform 0.5s ease-in-out';
                mask.style.transform = \`translateX(-\${currentSlide * (100/slidesPerView)}%)\`;
                mask.setAttribute('data-current-slide', currentSlide);
                
                event.preventDefault(); // Prevent default link behavior
              `
            }
          }
        ]
      },
      attributes: {
        'href': '#',
        'aria-label': direction === 'left' ? 'Previous slide' : 'Next slide',
        'data-direction': direction
      }
    };
  };

  // Update arrow image element to use modern, clear chevron icons
  const createArrowImage = (id, parentId, direction) => ({
    id,
    builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'image',
    text: '',
    styles: {
      width: '28px',
      height: '28px',
      objectFit: 'contain',
    },
    src: direction === 'left' 
      ? 'https://cdn-icons-png.flaticon.com/512/2985/2985161.png'  // Modern chevron left
      : 'https://cdn-icons-png.flaticon.com/512/2985/2985162.png', // Modern chevron right
    children: [],
    parentId: parentId,
    visibility: 'visible',
    hierarchyName: getHierarchyName('image'),
    attributes: {
      'alt': direction === 'left' ? 'Previous slide' : 'Next slide',
      'data-direction': direction
    }
  });
  
  // Create left and right arrows
  const leftArrow = createArrow(leftArrowId, 'left');
  const rightArrow = createArrow(rightArrowId, 'right');

  // Create left and right arrow image elements
  const leftArrowImage = createArrowImage(leftArrowId + 1000, leftArrowId, 'left');
  const rightArrowImage = createArrowImage(rightArrowId + 1000, rightArrowId, 'right');

  // Main structural elements
  const mainElements = [
    // Main Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Testimonial Slider Section',
      parentId: null,
      className: testimonialSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'testimonial-section',
        'data-component': 'section',
        'data-section-type': 'testimonial',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Testimonial Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialContainerClass,
      styles: {},
      children: [headerId, sliderId, controlsId],
      text: '',
      attributes: {
        'data-element': 'testimonial-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Testimonial Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'testimonial-header'
      }
    },
    
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Testimonial Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: testimonialTitleClass,
      styles: {},
      children: [],
      text: 'What Our Clients Are Saying',
      attributes: {
        'data-element': 'testimonial-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Testimonial Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: testimonialSubtitleClass,
      styles: {},
      children: [],
      text: 'Discover why businesses trust our platform to create beautiful and effective websites.',
      attributes: {
        'data-element': 'testimonial-subtitle'
      }
    },
    
    // Slider Container
    {
      id: sliderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'slider',
      name: 'Testimonial Slider',
      parentId: containerId,
      hierarchyName: getHierarchyName('slider'),
      className: testimonialSliderClass,
      sliderRole: 'container',
      styles: {},
      children: [maskId, leftArrowId, rightArrowId],
      text: '',
      slidesPerView: 2,
      slidesPerScroll: 1,
      deviceSettings: {
        desktop: {
          slidesPerView: 2,
          slidesPerScroll: 1
        },
        tablet: {
          slidesPerView: 1,
          slidesPerScroll: 1
        },
        mobile: {
          slidesPerView: 1,
          slidesPerScroll: 1
        }
      },
      attributes: {
        'data-element': 'testimonial-slider',
        'data-element-type': 'slider',
        'data-loop': 'true',
        'data-autoplay': 'false',
        'data-delay': '5000',
        'data-slides-per-view': '2'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 100,
            type: 'custom',
            action: 'custom',
            label: 'Initialize slider drag and autoplay functionality',
            value: {
              script: `
                // Initialize variables for drag functionality
                let isDragging = false;
                let startPosition = 0;
                let currentTranslate = 0;
                let prevTranslate = 0;
                let animationID = 0;
                let autoplayInterval = null;

                const slider = event.currentTarget;
                const mask = slider.querySelector('[data-slider-role="mask"]');
                if (!mask) return;

                // Add touch and mouse event listeners
                mask.addEventListener('mousedown', dragStart);
                mask.addEventListener('touchstart', dragStart, { passive: true });
                mask.addEventListener('mouseup', dragEnd);
                mask.addEventListener('touchend', dragEnd);
                mask.addEventListener('mouseleave', dragEnd);
                mask.addEventListener('mousemove', drag);
                mask.addEventListener('touchmove', drag, { passive: true });

                // Function to handle drag start
                function dragStart(e) {
                  e.preventDefault();
                  if (e.type === 'touchstart') {
                    startPosition = e.touches[0].clientX;
                  } else {
                    startPosition = e.clientX;
                  }
                  
                  // Stop autoplay during drag
                  if (autoplayInterval) {
                    clearInterval(autoplayInterval);
                  }
                  
                  isDragging = true;
                  
                  // Get current transform value
                  const style = window.getComputedStyle(mask);
                  const matrix = new WebKitCSSMatrix(style.transform);
                  prevTranslate = matrix.m41; // Get the X translation value
                  
                  cancelAnimationFrame(animationID);
                }

                // Function to handle dragging
                function drag(e) {
                  if (!isDragging) return;
                  
                  let currentPosition;
                  if (e.type === 'touchmove') {
                    currentPosition = e.touches[0].clientX;
                  } else {
                    currentPosition = e.clientX;
                  }
                  
                  // Calculate drag distance
                  const diff = currentPosition - startPosition;
                  
                  // Calculate what the new position would be
                  currentTranslate = prevTranslate + diff;
                  
                  // Apply the transform without transition for smooth dragging
                  mask.style.transition = 'none';
                  mask.style.transform = \`translateX(\${currentTranslate}px)\`;
                }

                // Function to handle drag end with snap-to-slide
                function dragEnd() {
                  if (!isDragging) return;
                  
                  isDragging = false;
                  
                  // Re-enable transition for smooth snap
                  mask.style.transition = 'transform 0.3s ease-out';
                  
                  const loopEnabled = slider.getAttribute('data-loop') === 'true';
                  let slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || 2);
                  
                  // Adjust for mobile
                  if (window.innerWidth <= 768) {
                    slidesPerView = 1;
                  }
                  
                  const slides = mask.children.length;
                  
                  // Calculate the width of a single slide
                  const maskWidth = mask.offsetWidth;
                  const slideWidth = maskWidth / slidesPerView;
                  
                  // Calculate the slide to snap to based on current position
                  let currentSlide = parseInt(mask.getAttribute('data-current-slide') || '0');
                  
                  // Calculate movement threshold - if moved more than 20% of slide width
                  const moveThreshold = slideWidth * 0.2;
                  const movement = Math.abs(currentTranslate - prevTranslate);
                  
                  if (movement > moveThreshold) {
                    // Determine direction of swipe
                    if (currentTranslate > prevTranslate) {
                      // Swiped right (previous slide)
                      if (currentSlide === 0 && loopEnabled) {
                        currentSlide = slides - slidesPerView;
                      } else {
                        currentSlide = Math.max(0, currentSlide - 1);
                      }
                    } else {
                      // Swiped left (next slide)
                      if (currentSlide >= slides - slidesPerView && loopEnabled) {
                        currentSlide = 0;
                      } else {
                        currentSlide = Math.min(slides - slidesPerView, currentSlide + 1);
                      }
                    }
                  }
                  
                  // Move to the slide position using correct calculation for multiple slides
                  mask.style.transform = \`translateX(-\${currentSlide * (100/slidesPerView)}%)\`;
                  mask.setAttribute('data-current-slide', currentSlide);
                  
                  // Restart autoplay if enabled
                  initAutoplay();
                }

                // Function to go to a specific slide
                function goToSlide(slideIndex) {
                  const slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || 2);
                  mask.style.transform = \`translateX(-\${slideIndex * (100/slidesPerView)}%)\`;
                  mask.setAttribute('data-current-slide', slideIndex);
                }

                // Function to handle autoplay
                function initAutoplay() {
                  const autoplayEnabled = slider.getAttribute('data-autoplay') === 'true';
                  const autoplayDelay = parseInt(slider.getAttribute('data-delay') || '5000');
                  const loopEnabled = slider.getAttribute('data-loop') === 'true';
                  let slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || 2);
                  
                  // Adjust for mobile
                  if (window.innerWidth <= 768) {
                    slidesPerView = 1;
                  }
                  
                  if (autoplayInterval) {
                    clearInterval(autoplayInterval);
                    autoplayInterval = null;
                  }
                  
                  if (autoplayEnabled) {
                    autoplayInterval = setInterval(() => {
                      const slides = mask.children.length;
                      let currentSlide = parseInt(mask.getAttribute('data-current-slide') || '0');
                      
                      // Calculate the next slide
                      if (currentSlide >= slides - slidesPerView) {
                        if (loopEnabled) {
                          currentSlide = 0;
                        } else {
                          // Stop autoplay once reached the end if not looping
                          clearInterval(autoplayInterval);
                          return;
                        }
                      } else {
                        currentSlide++;
                      }
                      
                      // Move to the next slide with correct calculation for multiple slides
                      mask.style.transform = \`translateX(-\${currentSlide * (100/slidesPerView)}%)\`;
                      mask.setAttribute('data-current-slide', currentSlide);
                    }, autoplayDelay);
                  }
                }

                // Initialize autoplay if enabled
                initAutoplay();

                // Re-initialize on window resize
                window.addEventListener('resize', () => {
                  if (isDragging) dragEnd();
                  setTimeout(initAutoplay, 100);
                });
              `
            }
          }
        ]
      }
    },
    
    // Slider Mask
    {
      id: maskId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Slider Mask',
      parentId: sliderId,
      hierarchyName: getHierarchyName('div'),
      sliderRole: 'mask',
      styles: {
        position: 'absolute',
        display: 'flex',
        width: '100%',
        height: '100%',
        transition: 'transform 0.5s ease-in-out',
        cursor: 'grab'
      },
      children: [slide1Id, slide2Id, slide3Id, slide4Id],
      text: '',
      attributes: {
        'data-element': 'slider-mask',
        'data-current-slide': '0',
        'data-slides-count': '4'
      }
    },
    
    // Left slider arrow
    leftArrow,
    
    // Right slider arrow
    rightArrow,
    
    // Controls container
    {
      id: controlsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Slider Controls',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialControlsClass,
      styles: {},
      children: [autoplayToggleId, loopToggleId],
      attributes: {
        'data-element': 'testimonial-controls'
      }
    },
    
    // Autoplay toggle
    {
      id: autoplayToggleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Autoplay Toggle',
      parentId: controlsId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      },
      children: [],
      text: '',
      attributes: {
        'data-element': 'autoplay-toggle'
      },
      innerHTML: `
        <label for="autoplay-toggle-${sectionId}" style="font-size: 14px; color: #333; font-weight: 500;">Autoplay</label>
        <input type="checkbox" id="autoplay-toggle-${sectionId}" style="cursor: pointer; accent-color: #4f46e5;" onchange="
          const slider = document.querySelector('#${sliderId}');
          if (slider) {
            slider.setAttribute('data-autoplay', this.checked ? 'true' : 'false');
            
            // Dispatch a custom event to notify the slider to update autoplay
            const event = new CustomEvent('autoplayToggle', { detail: { enabled: this.checked } });
            slider.dispatchEvent(event);
            
            // Initialize autoplay based on new setting
            if (this.checked) {
              const delay = parseInt(slider.getAttribute('data-delay') || '5000');
              const mask = slider.querySelector('[data-slider-role=\\'mask\\']');
              if (mask) {
                let autoplayInterval = window[slider.id + '_autoplay'];
                if (autoplayInterval) {
                  clearInterval(autoplayInterval);
                }
                
                autoplayInterval = setInterval(() => {
                  const loopEnabled = slider.getAttribute('data-loop') === 'true';
                  let slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || 2);
                  
                  // Adjust for mobile
                  if (window.innerWidth <= 768) {
                    slidesPerView = 1;
                  }
                  
                  const slides = mask.children.length;
                  let currentSlide = parseInt(mask.getAttribute('data-current-slide') || '0');
                  
                  if (currentSlide >= slides - slidesPerView) {
                    if (loopEnabled) {
                      currentSlide = 0;
                    } else {
                      clearInterval(autoplayInterval);
                      return;
                    }
                  } else {
                    currentSlide++;
                  }
                  
                  mask.style.transform = \`translateX(-\${currentSlide * (100/slidesPerView)}%)\`;
                  mask.setAttribute('data-current-slide', currentSlide);
                }, delay);
                
                window[slider.id + '_autoplay'] = autoplayInterval;
              }
            } else {
              let autoplayInterval = window[slider.id + '_autoplay'];
              if (autoplayInterval) {
                clearInterval(autoplayInterval);
                window[slider.id + '_autoplay'] = null;
              }
            }
          }
        ">
      `
    },
    
    // Loop toggle
    {
      id: loopToggleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Loop Toggle',
      parentId: controlsId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      },
      children: [],
      text: '',
      attributes: {
        'data-element': 'loop-toggle'
      },
      innerHTML: `
        <label for="loop-toggle-${sectionId}" style="font-size: 14px; color: #333; font-weight: 500;">Loop Slides</label>
        <input type="checkbox" id="loop-toggle-${sectionId}" checked style="cursor: pointer; accent-color: #4f46e5;" onchange="
          const slider = document.querySelector('#${sliderId}');
          if (slider) {
            slider.setAttribute('data-loop', this.checked ? 'true' : 'false');
          }
        ">
      `
    }
  ];

  // First add the slides to the elements array
  const newElements = [
    ...mainElements,
    slide1,
    slide2,
    slide3,
    slide4
  ];

  // Add all card elements
  const cardElements = [
    ...card1Elements,
    ...card2Elements,
    ...card3Elements,
    ...card4Elements
  ];

  // Add the arrow images
  const arrowElements = [
    leftArrowImage,
    rightArrowImage
  ];

  // Combine all elements
  const allElements = [
    ...newElements,
    ...cardElements,
    ...arrowElements
  ];
  
  return {
    testimonialSection: allElements[0],
    elements: allElements
  };
};

export default createTestimonialElements; 